import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import placeholderLogo from '../../assets/unknown_company.png';
import { updateInvestigation } from '../../api/investigationsService';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';

interface Investigation {
  id: string;
  company_name: string;
  domains: string[];
  urls: string[];
  comments?: string;
  created_at: string;
  status: string;
}

interface InvestigationDetailsModalProps {
  investigation: Investigation;
  logoUrl?: string | null;
  onClose: () => void;
  onUpdate?: (updatedInvestigation: Investigation) => void;
}

const InvestigationDetailsModal: React.FC<InvestigationDetailsModalProps> = ({
  investigation,
  logoUrl,
  onClose,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [companyName, setCompanyName] = useState(investigation.company_name);
  const [comments, setComments] = useState(investigation.comments || '');
  const [domains, setDomains] = useState<string[]>(
    investigation.domains.length > 0 ? investigation.domains : ['']
  );
  const [urls, setUrls] = useState<string[]>(
    investigation.urls.length > 0 ? investigation.urls : ['']
  );
  const [loading, setLoading] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updateData = {
        company_name: companyName,
        comments,
        domains: domains.filter((d) => d.trim() !== ''),
        urls: urls.filter((u) => u.trim() !== ''),
      };

      const updatedInvestigation = await updateInvestigation(investigation.id, updateData);
      toast.success(t('investigation.updated_success'));
      if (onUpdate) {
        onUpdate(updatedInvestigation);
      }
      setIsEditing(false);
    } catch (error: any) {
      toast.error(error.message || t('investigation.update_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = () => setDomains([...domains, '']);
  const handleDomainChange = (index: number, value: string) => {
    const newDomains = [...domains];
    newDomains[index] = value;
    setDomains(newDomains);
  };
  const handleRemoveDomain = (index: number) => {
    const newDomains = [...domains];
    newDomains.splice(index, 1);
    setDomains(newDomains);
  };

  const handleAddUrl = () => setUrls([...urls, '']);
  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
  };
  const handleRemoveUrl = (index: number) => {
    const newUrls = [...urls];
    newUrls.splice(index, 1);
    setUrls(newUrls);
  };

  const renderStatusMessage = () => {
    const requestDate = new Date(investigation.created_at);
    const reviewDeadline = new Date(requestDate);
    reviewDeadline.setDate(requestDate.getDate() + 2);
    const formattedReviewDeadline = reviewDeadline.toLocaleDateString();
  
    switch (investigation.status) {
      case 'under_review':
        return (
          <p className="text-gray-700 text-lg text-justify">
            <Trans
              i18nKey="investigation.under_review"
              values={{ companyName, deadline: formattedReviewDeadline }}
              components={{ strong: <strong /> }}
            />
          </p>
        );
      case 'accepted':
        return (
          <p className="text-gray-700 text-lg text-justify">
            <Trans
              i18nKey="investigation.accepted"
              values={{ companyName }}
              components={{ strong: <strong /> }}
            />
          </p>
        );
      case 'rejected':
        return (
          <p className="text-gray-700 text-lg text-justify">
            <Trans
              i18nKey="investigation.rejected"
              values={{ companyName }}
              components={{ strong: <strong /> }}
            />
          </p>
        );
      case 'canceled':
        return (
          <p className="text-gray-700 text-lg text-justify">
            <Trans
              i18nKey="investigation.canceled"
              values={{ companyName }}
              components={{ strong: <strong /> }}
            />
          </p>
        );
      default:
        return null;
    }
  };
  

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 pointer-events-auto">
      <div className="fixed inset-0 bg-black bg-opacity-60 pointer-events-auto" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-lg max-w-2xl w-full overflow-hidden z-[10000]">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-4">
            <img
              src={logoUrl || placeholderLogo}
              alt={companyName}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              {isEditing ? (
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="text-2xl font-bold border-b border-gray-300 focus:outline-none"
                />
              ) : (
                <h2 className="text-2xl font-bold">{companyName}</h2>
              )}
              <p className="text-sm text-gray-500">{t('investigation.details_subheading')}</p>
            </div>
          </div>
          <div className="flex space-x-2">
            {!isEditing && investigation.status === 'under_review' && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                {t('investigation.edit')}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
            >
              {t('investigation.close')}
            </button>
          </div>
        </div>

        <div className="max-h-[80vh] overflow-y-auto px-6 py-4">
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-blue-600 mb-2">{t('investigation.status_heading')}</h4>
            {renderStatusMessage()}
          </div>

          <div className="border-t my-4"></div>

          {/* Dominios */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-blue-600 mb-2">{t('investigation.domains_heading')}</h4>
            {isEditing ? (
              <div className="space-y-2">
                {domains.map((domain, index) => (
                  <div key={index} className="flex items-center mb-1">
                    <input
                      type="text"
                      value={domain}
                      onChange={(e) => handleDomainChange(index, e.target.value)}
                      className="p-2 border border-gray-300 rounded"
                    />
                    {domains.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveDomain(index)}
                        className="ml-2 text-red-600 hover:text-red-800"
                      >
                        {t('investigation.delete')}
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddDomain}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-800"
                >
                  {t('investigation.add_domain')}
                </button>
              </div>
            ) : (
              <>
                {domains.length > 0 ? (
                  <div className="space-y-2">
                    {domains.map((domain, index) => (
                      <div key={index} className="p-2 bg-gray-50 rounded-md">
                        {domain}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">{t('investigation.no_domains')}</p>
                )}
              </>
            )}
          </div>

          <div className="border-t my-4"></div>

          {/* URLs */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-blue-600 mb-2">{t('investigation.urls_heading')}</h4>
            {isEditing ? (
              <div className="space-y-2">
                {urls.map((url, index) => (
                  <div key={index} className="flex items-center mb-1">
                    <input
                      type="text"
                      value={url}
                      onChange={(e) => handleUrlChange(index, e.target.value)}
                      className="p-2 border border-gray-300 rounded"
                    />
                    {urls.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveUrl(index)}
                        className="ml-2 text-red-600 hover:text-red-800"
                      >
                        {t('investigation.delete')}
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddUrl}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-800"
                >
                  {t('investigation.add_url')}
                </button>
              </div>
            ) : (
              <>
                {urls.length > 0 ? (
                  <div className="space-y-2">
                    {urls.map((url, index) => (
                      <div key={index} className="p-2 bg-gray-50 rounded-md break-all">
                        {url}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">{t('investigation.no_urls')}</p>
                )}
              </>
            )}
          </div>

          <div className="border-t my-4"></div>

          {/* Comentarios */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-blue-600 mb-2">{t('investigation.comments_heading')}</h4>
            {isEditing ? (
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
                rows={4}
              />
            ) : (
              <p className="text-gray-700 whitespace-pre-wrap text-justify">{comments}</p>
            )}
          </div>

          <div className="border-t my-4"></div>

          {/* Fecha de Solicitud */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-blue-600 mb-2">{t('investigation.request_date_heading')}</h4>
            <p className="text-gray-700">
              {new Date(investigation.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {isEditing && (
          <div className="flex justify-end p-6 border-t space-x-2">
            <button
              onClick={() => setIsEditing(false)}
              className="px-6 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition duration-200 focus:outline-none"
            >
              {t('investigation.cancel_edit')}
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 focus:outline-none"
            >
              {loading ? t('investigation.saving') : t('investigation.save_changes')}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default InvestigationDetailsModal;
